<template>
  <!-- The async-setup screen is swapped in at the root once the org probe
       resolves; a stable root element keeps the route <Suspense> anchor
       valid (see .stable-route-root in global.scss). -->
  <div class="stable-route-root">
    <div v-if="entitlementBlocked" />
    <AgentConversation
      v-else-if="organizationId && conversationId"
      :key="`${organizationId}:${agentId}`"
      :organization-id="organizationId"
      :agent-id="agentId"
      :conversation-id="conversationId"
    />
  </div>
</template>

<script lang="ts">
import { computedAsync } from "@vueuse/core";
import { computed, defineComponent, ref, watch } from "vue";
import { useRoute } from "vue-router";

import type { OrgId } from "@ctrl/controller-rpc";

import AgentConversation from "@/components/ai/AgentConversation.vue";
import { isAssistant, resolveAgentOrganization } from "@/components/ai/agents";
import * as inject from "@/components/inject";
import { openUpgradeModal } from "@/components/modals/upgrade";
import { waitForEntitlement } from "@/utils/billing";

const ENTITLEMENT_KEY_AGENTS = "ai-agents";
const ENTITLEMENT_KEY_ASSISTANT = "ai-assistant";

export default defineComponent({
  name: "AgentConversationRoute",
  components: { AgentConversation },
  props: {
    agentId: { type: String, required: true },
  },
  setup(props) {
    const controller = inject.controller();
    const profile = inject.profile();
    const billing = inject.billing();
    const route = useRoute();

    const conversationId = computed(() =>
      typeof route.query.id === "string" ? route.query.id : null
    );
    const explicitOrganizationId = computed(() =>
      typeof route.query.organizationId === "string"
        ? route.query.organizationId
        : null
    );

    const organizationId = computedAsync<OrgId | null>(
      () =>
        explicitOrganizationId.value ??
        resolveAgentOrganization(controller, profile.value, props.agentId),
      null
    );
    const entitlementBlocked = ref(false);

    watch(
      () => [props.agentId, organizationId.value, billing.value] as const,
      async ([agentId, org]) => {
        if (!org) return;
        const key = isAssistant(agentId)
          ? ENTITLEMENT_KEY_ASSISTANT
          : ENTITLEMENT_KEY_AGENTS;
        // Assistant is account-level (any org's plan); agents stay org-scoped.
        const entitlementOrg = isAssistant(agentId) ? undefined : org;
        // Waits for the entitlement to be known; still-unknown fails open
        // (see waitForEntitlement).
        const entitled = await waitForEntitlement(billing, key, entitlementOrg);
        if (!entitled) {
          openUpgradeModal(billing, entitlementOrg, key);
          entitlementBlocked.value = true;
        } else {
          entitlementBlocked.value = false;
        }
      },
      { immediate: true }
    );

    return { organizationId, conversationId, entitlementBlocked };
  },
});
</script>
